import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosInstance } from '../../axios';
import { AdAsset, AdAssetRequest, AdCreativesApiResponse, DeleteResult } from './types';

export const AD_ASSETS_QUERY_KEY = ['ad-assets'] as const;

/**
 * Biblioteka zdjęć generatora. Czytamy własny rejestr, a nie katalog Bytescale:
 * listowanie folderu wymaga klucza `secret_*`, a panel ma tylko publiczny
 * (upload i odczyt pliku).
 */
export const fetchAdAssets = async (): Promise<AdAsset[]> => {
  const res = await axiosInstance.get<AdCreativesApiResponse<AdAsset[]>>('/v3-getAdAssets');
  const data = res.data?.data;
  if (!Array.isArray(data)) {
    throw new Error('Nieoczekiwany kształt odpowiedzi /v3-getAdAssets');
  }
  return data;
};

export const useGetAdAssets = (): UseQueryResult<AdAsset[], AxiosError> => useQuery<AdAsset[], AxiosError>({
  queryKey: AD_ASSETS_QUERY_KEY,
  queryFn: fetchAdAssets,
  refetchOnWindowFocus: false,
});

/**
 * Dopisuje wgrany plik do biblioteki. Same bajty idą z przeglądarki wprost do
 * Bytescale — tutaj leci wyłącznie metryczka (adres, wymiary, waga).
 */
export const usePostAdAsset = (
  options?: UseMutationOptions<AdAsset, AxiosError, { data: AdAssetRequest }>,
): UseMutationResult<AdAsset, AxiosError, { data: AdAssetRequest }> => {
  const queryClient = useQueryClient();

  return useMutation<AdAsset, AxiosError, { data: AdAssetRequest }>({
    mutationFn: async ({ data }) => {
      const res = await axiosInstance.post<AdCreativesApiResponse<AdAsset>>('/v3-createAdAsset', data);
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: AD_ASSETS_QUERY_KEY });
      options?.onSuccess?.(...args);
    },
  });
};

/** Zdejmuje zdjęcie z biblioteki. Plik w Bytescale zostaje, bo używają go zapisane kreacje. */
export const useDeleteAdAssets = (
  options?: UseMutationOptions<DeleteResult, AxiosError, { ids: string[] }>,
): UseMutationResult<DeleteResult, AxiosError, { ids: string[] }> => {
  const queryClient = useQueryClient();

  return useMutation<DeleteResult, AxiosError, { ids: string[] }>({
    mutationFn: async ({ ids }) => {
      const res = await axiosInstance.delete<AdCreativesApiResponse<DeleteResult>>('/v3-deleteAdAssets', {
        data: { ids },
      });
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: AD_ASSETS_QUERY_KEY });
      options?.onSuccess?.(...args);
    },
  });
};
